import { createContext, useContext, useState, useMemo } from 'react'
import { ReviewsWidgetConfig } from './config'
import { Review } from './data'

type ReviewsContextType = ReviewsWidgetConfig & {
  onReadMore?: (review: Review) => void
  showPropertyName: boolean
  detailReview: Review | null
  setDetailReview: (review: Review | null) => void
  resolvedTheme?: 'light' | 'dark'
  /** Debounced keyword applied to the loaded reviews (#1692). Empty = no filter. */
  searchQuery: string
  setSearchQuery: (query: string) => void
}

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined)

export function useReviewsConfig() {
  const context = useContext(ReviewsContext)
  if (!context) {
    throw new Error('useReviewsConfig must be used within a ReviewsProvider')
  }
  return context
}

export function ReviewsProvider({
  children,
  options,
  resolvedTheme,
}: {
  children: React.ReactNode
  options: ReviewsWidgetConfig
  resolvedTheme?: 'light' | 'dark'
}) {
  const [detailReview, setDetailReview] = useState<Review | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const showPropertyName = useMemo(() => {
    return !options.filter.property && !options.hideProperty
  }, [options.filter.property, options.hideProperty])

  const onReadMore = useMemo(() => {
    return options.popupForDetail ? (review: Review) => setDetailReview(review) : undefined
  }, [options.popupForDetail])

  const contextValue = useMemo(() => ({
    ...options,
    colorScheme: resolvedTheme ?? options.colorScheme,
    onReadMore,
    showPropertyName,
    detailReview,
    setDetailReview,
    resolvedTheme,
    searchQuery: options.showSearch ? searchQuery : '',
    setSearchQuery,
  }), [options, onReadMore, showPropertyName, detailReview, resolvedTheme, searchQuery])

  return <ReviewsContext.Provider value={contextValue}>{children}</ReviewsContext.Provider>
}
